import React, { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { useT } from "../lib/i18n";
import { openBooking } from "../lib/booking";

const LANGS = [
    { code: "en", label: "EN" },
    { code: "it", label: "IT" },
    { code: "de", label: "DE" },
    { code: "es", label: "ES" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const { t, lang, setLang } = useT();
    const navigate = useNavigate();

    const NAV = [
        { to: "/", key: "home", label: t.nav.home },
        { to: "/about", key: "about", label: t.nav.about },
        { to: "/services", key: "services", label: t.nav.services },
        { to: "/insights", key: "insights", label: t.nav.insights },
        { to: "/contact", key: "contact", label: t.nav.contact },
    ];

    function handleBook(e) {
        e.preventDefault();
        setOpen(false);
        openBooking(navigate);
    }

    return (
        <header
            className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#F9F9F7]/80 border-b hair"
            data-testid="site-header"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 h-20 flex items-center justify-between gap-4">
                <Link to="/" data-testid="nav-home-logo" aria-label="Casati Consulting home">
                    <Logo />
                </Link>

                <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === "/"}
                            data-testid={`nav-${n.key}`}
                            className={({ isActive }) =>
                                `link-underline text-[13px] font-medium tracking-wide ${
                                    isActive ? "text-[#1C1C1C]" : "text-[#1C1C1C]/70 hover:text-[#1C1C1C]"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <span data-active={isActive ? "true" : "false"}>{n.label}</span>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3 sm:gap-4">
                    <div
                        className="hidden sm:flex items-center text-[10px] uppercase tracking-[0.18em] font-bold"
                        role="group"
                        aria-label="Language switcher"
                        data-testid="lang-switcher"
                    >
                        {LANGS.map((l, i) => (
                            <React.Fragment key={l.code}>
                                {i > 0 && <span className="text-[#1C1C1C]/25">·</span>}
                                <button
                                    type="button"
                                    onClick={() => setLang(l.code)}
                                    className={`px-1.5 py-1 transition-colors ${
                                        lang === l.code
                                            ? "text-[#9B7A58]"
                                            : "text-[#1C1C1C]/40 hover:text-[#1C1C1C]"
                                    }`}
                                    data-testid={`lang-${l.code}`}
                                    aria-pressed={lang === l.code}
                                >
                                    {l.label}
                                </button>
                            </React.Fragment>
                        ))}
                    </div>
                    <div
                        className="flex sm:hidden items-center text-[10px] uppercase tracking-[0.2em] font-bold"
                        role="group"
                        aria-label="Language switcher"
                        data-testid="lang-switcher-mobile-inline"
                    >
                        {LANGS.map((l, i) => (
                            <React.Fragment key={l.code}>
                                {i > 0 && <span className="text-[#1C1C1C]/25 px-0.5">·</span>}
                                <button
                                    type="button"
                                    onClick={() => setLang(l.code)}
                                    className={`px-1 py-1 transition-colors ${
                                        lang === l.code
                                            ? "text-[#9B7A58]"
                                            : "text-[#1C1C1C]/45"
                                    }`}
                                    data-testid={`lang-inline-${l.code}`}
                                    aria-pressed={lang === l.code}
                                >
                                    {l.label}
                                </button>
                            </React.Fragment>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={handleBook}
                        className="hidden lg:inline-flex group items-center gap-2 px-5 h-10 bg-[#1C1C1C] text-[#F9F9F7] text-[12px] uppercase tracking-[0.18em] font-bold hover:bg-[#9B7A58] transition-colors duration-300"
                        data-testid="header-cta-book"
                    >
                        {t.cta.book}
                        <ArrowUpRight
                            size={14}
                            strokeWidth={1.5}
                            className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                        />
                    </button>
                    <button
                        type="button"
                        className="lg:hidden p-2 -mr-2 text-[#1C1C1C]"
                        onClick={() => setOpen((o) => !o)}
                        data-testid="mobile-menu-toggle"
                        aria-label={open ? t.common.close : t.common.menu}
                    >
                        {open ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t hair bg-[#F9F9F7]" data-testid="mobile-menu">
                    <nav className="px-6 py-6 flex flex-col gap-5" aria-label="Mobile">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${n.key}`}
                                className={({ isActive }) =>
                                    `text-base font-medium ${
                                        isActive ? "text-[#9B7A58]" : "text-[#1C1C1C]"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <div className="flex items-center gap-2 pt-4 text-[10px] uppercase tracking-[0.18em] font-bold flex-wrap">
                            {LANGS.map((l, i) => (
                                <React.Fragment key={l.code}>
                                    {i > 0 && <span className="text-[#1C1C1C]/25">·</span>}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setLang(l.code);
                                            setOpen(false);
                                        }}
                                        className={lang === l.code ? "text-[#9B7A58]" : "text-[#1C1C1C]/50"}
                                        data-testid={`mobile-lang-${l.code}`}
                                    >
                                        {l.label}
                                    </button>
                                </React.Fragment>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={handleBook}
                            className="mt-2 inline-flex items-center justify-center h-12 bg-[#1C1C1C] text-[#F9F9F7] text-[12px] uppercase tracking-[0.18em] font-bold"
                            data-testid="mobile-cta-book"
                        >
                            {t.cta.book}
                        </button>
                    </nav>
                </div>
            )}
        </header>
    );
}
